import { useEffect, useState } from 'react';
import api from '../api/axios';
import './Payments.css';

const currency = (value) =>
  `Rs. ${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function StatusBadge({ status }) {
  const key = String(status || '').toLowerCase().replace(/\s+/g, '-');
  return <span className={`fin-badge fin-badge-${key || 'pending'}`}>{status || 'Pending'}</span>;
}

function Card({ children, className = '' }) {
  return <section className={`fin-card ${className}`}>{children}</section>;
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="fin-section-title">
      <h2>{title}</h2>
      {subtitle && <p>{subtitle}</p>}
    </div>
  );
}

function KpiCard({ item }) {
  return (
    <Card className={`fin-kpi fin-kpi-${item.tone || 'purple'}`}>
      <div className="fin-kpi-top">
        <span>{item.label}</span>
        <i />
      </div>
      <strong>{currency(item.value)}</strong>
      <p>{item.meta}</p>
    </Card>
  );
}

function RevenueTrendChart({ trend = [] }) {
  const data = trend.length ? trend : [{ month: 'Current', revenue: 0, received: 0 }];
  const max = Math.max(1, ...data.map((row) => Number(row.revenue || row.received || 0)));
  const latestMonth = data[data.length - 1] || {};

  return (
    <Card className="fin-chart-card">
      <div className="fin-chart-head">
        <SectionTitle title="Revenue Trend" subtitle="Revenue vs received amount" />
        <div className="fin-mini-stat">
          <span>{latestMonth.month || 'Current'} income</span>
          <strong>{currency(latestMonth.received || latestMonth.revenue || 0)}</strong>
        </div>
      </div>
      <div className="fin-bar-chart">
        {data.map((row, idx) => (
          <div className="fin-bar-group" key={row.month || idx}>
            <div className="fin-bars">
              <span className="fin-bar fin-bar-revenue" style={{ height: `${Math.max(12, (Number(row.revenue || 0) / max) * 100)}%` }} />
              <span className="fin-bar fin-bar-received" style={{ height: `${Math.max(12, (Number(row.received || 0) / max) * 100)}%` }} />
            </div>
            <small>{row.month}</small>
          </div>
        ))}
      </div>
      <div className="fin-legend">
        <span><i className="fin-dot fin-dot-purple" />Invoiced</span>
        <span><i className="fin-dot fin-dot-green" />Received</span>
      </div>
    </Card>
  );
}

function ExpenseBreakdownChart({ expenses = [] }) {
  const defaultBreakdown = [
    { label: 'Procurement Orders', pct: 55, tone: 'purple' },
    { label: 'Labour / Work Orders', pct: 30, tone: 'amber' },
    { label: 'Site Transport & Misc', pct: 15, tone: 'blue' },
  ];
  const items = expenses.length ? expenses : defaultBreakdown;

  return (
    <Card className="fin-chart-card">
      <SectionTitle title="Expense Categories" subtitle="Cost distribution across operations" />
      <div className="fin-progress-list" style={{ marginTop: '1rem' }}>
        {items.map((item) => (
          <div className="fin-progress-row" key={item.label}>
            <div>
              <span>{item.label}</span>
              <strong>{item.value ? currency(item.value) + ' · ' : ''}{item.pct}%</strong>
            </div>
            <em><i className={`fin-fill fin-fill-${item.tone || 'purple'}`} style={{ width: `${item.pct}%` }} /></em>
          </div>
        ))}
      </div>
    </Card>
  );
}

function InsightCard({ label, value, note }) {
  return (
    <div className="fin-insight">
      <span>{label}</span>
      <strong>{value}</strong>
      <p>{note}</p>
    </div>
  );
}

function ReceivablesAging({ aging = [] }) {
  const data = aging.length ? aging : [
    { bucket: '0-15 days', amount: 0, pct: 100 }
  ];

  return (
    <Card className="fin-chart-card">
      <SectionTitle title="Receivables Aging" subtitle="Pending collections grouped by age" />
      <div className="fin-progress-list" style={{ marginTop: '1rem' }}>
        {data.map((item) => (
          <div className="fin-progress-row" key={item.bucket}>
            <div>
              <span>{item.bucket}</span>
              <strong>{currency(item.amount)} ({item.pct}%)</strong>
            </div>
            <em><i className="fin-fill fin-fill-purple" style={{ width: `${Math.min(100, Math.max(5, item.pct))}%` }} /></em>
          </div>
        ))}
      </div>
    </Card>
  );
}

function CompactList({ title, rows = [], emptyText = 'No items found.' }) {
  return (
    <Card className="fin-list-card">
      <SectionTitle title={title} />
      <div className="fin-list">
        {rows.length === 0 ? (
          <p style={{ padding: '1rem', color: 'var(--muted)', fontSize: '0.82rem', textAlign: 'center' }}>{emptyText}</p>
        ) : (
          rows.map((row, i) => (
            <div className="fin-list-row" key={row._id || i}>
              <div>
                <strong>{row.client || row.vendor || row.description}</strong>
                <span>{row.dueDate || row.date}{row.type ? ` · ${row.type}` : ''}</span>
              </div>
              <aside>
                <b className={row.amount < 0 ? 'fin-negative' : ''}>{currency(Math.abs(row.amount))}</b>
                {row.status && <StatusBadge status={row.status} />}
              </aside>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}

export default function Payments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/payments/stats')
      .then((r) => setData(r.data?.data))
      .catch((err) => console.error('Failed to load financial stats', err))
      .finally(() => setLoading(false));
  }, []);

  const kpis = data?.kpis || [
    { label: 'Total Revenue', value: 0, meta: 'All tracked revenue', tone: 'green' },
    { label: 'Amount Received', value: 0, meta: 'Total collected income', tone: 'blue' },
    { label: 'Pending Collection', value: 0, meta: 'Open invoices', tone: 'amber' },
    { label: 'Total Expenses', value: 0, meta: 'Total paid expenses', tone: 'red' },
    { label: 'Net Profit', value: 0, meta: 'Operating margin', tone: 'purple' },
    { label: 'Cash Flow', value: 0, meta: 'Net cash balance', tone: 'teal' },
  ];

  const businessStats = data?.businessStats || [
    ['Total Clients', 0],
    ['Active Projects', 0],
    ['Completed Projects', 0],
    ['Quotations', 0],
    ['Invoices', 0],
    ['Vendors', 0],
  ];

  return (
    <div className="fin-page slide-up">
      <div className="fin-shell">
        <header className="fin-hero">
          <div className="fin-hero-main">
            <span>Company Financials</span>
            <h1>Financials & Cash Flow</h1>
            <p>Live company revenue, milestone collections, operating expenses, and cash flow ledger.</p>
          </div>
        </header>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <div className="spinner spinner-lg" />
          </div>
        ) : (
          <>
            <div className="fin-kpi-grid">
              {kpis.map((item) => <KpiCard item={item} key={item.label} />)}
            </div>

            <div className="fin-grid fin-grid-charts">
              <RevenueTrendChart trend={data?.revenueTrend || []} />
              <ExpenseBreakdownChart />
            </div>

            <div className="fin-grid fin-grid-insights">
              <Card className="fin-insights-card">
                <SectionTitle title="Financial Health Overview" subtitle="Live health indicators across the company" />
                <div className="fin-insights-grid">
                  <InsightCard
                    label="Collection Rate"
                    value={kpis[0]?.value > 0 ? `${Math.round((kpis[1]?.value / kpis[0]?.value) * 100)}%` : '0%'}
                    note="Percentage of invoiced revenue successfully received."
                  />
                  <InsightCard
                    label="Net Margin"
                    value={kpis[0]?.value > 0 ? `${Math.round((kpis[4]?.value / kpis[0]?.value) * 100)}%` : '0%'}
                    note="Net profit margin after subtracting operational expenses."
                  />
                  <InsightCard
                    label="Pending Collections"
                    value={currency(kpis[2]?.value)}
                    note="Receivables currently due across active client accounts."
                  />
                  <InsightCard
                    label="Cash Flow Balance"
                    value={currency(kpis[5]?.value)}
                    note="Current liquidity balance from paid transactions."
                  />
                </div>
              </Card>
              <ReceivablesAging aging={data?.receivablesAging || []} />
            </div>

            <div className="fin-grid fin-grid-lists">
              <CompactList title="Upcoming Collections" rows={data?.upcomingCollections || []} emptyText="No pending collections." />
              <CompactList title="Vendor Payments" rows={data?.vendorPayments || []} emptyText="No vendor payments due." />
              <CompactList title="Recent Transactions" rows={data?.recentTransactions || []} emptyText="No recent transactions recorded." />
            </div>

            <div className="fin-grid fin-grid-bottom">
              <Card className="fin-stats-card">
                <SectionTitle title="Business Statistics" />
                <div className="fin-stats-grid">
                  {businessStats.map(([label, value]) => (
                    <div key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
              </Card>
              <Card className="fin-actions-card">
                <SectionTitle title="Quick Actions" />
                <button type="button" onClick={() => window.location.href = '/activity'}>Record Follow-up</button>
                <button type="button" onClick={() => window.location.href = '/projects'}>View Projects</button>
                <button type="button" onClick={() => window.location.href = '/clients'}>View Clients</button>
              </Card>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
